/**
 * Maps ADC conjugation sites onto markers on the antibody glyph.
 *
 * A site names a domain of the antibody format by label, and a residue within it. The antibody format has
 * no per-residue coordinates, and domains are folded, so where a residue falls along its sequence says
 * little about where it sits in the drawing. Markers therefore do not try to reflect the residue position
 * within the domain. Instead, the sites of each domain are drawn on every copy of it (e.g. on both heavy
 * chains' CH3), in the same place on each:
 *
 * - A chain domain or a hinge with a few sites gets a marker for each, spaced evenly in residue order: down
 *   the outside edge of a chain domain, or along and beside a hinge.
 * - A chain domain or a hinge with more sites gets a single solid marker with their number, where the only
 *   marker would be for a single site.
 * - Any other line (a linker, joiner, or spacer) gets a single marker, an ordinary one for one site and a
 *   solid one with their number for several, halfway along the part of the line that isn't hidden behind a
 *   chain domain, and a little way off the line.
 *
 * The hover label of a marker gives the chain, domain, and antibody numbering of its site, or lists its sites.
 *
 * Positions are computed in the viewbox of the antibody glyph (see Antibody.tsx), which draws each domain in
 * its own coordinate space and then rotates, reflects, and translates it into place. That transform is
 * mirrored by `applyDomainTransform` in conjugationSiteMarkerUtils, along with the other geometry the
 * placements are worked out with.
 */

import {
  AntibodyFormat,
  AntibodyFormatDomainVisualization,
  BlockDomainVisualization,
  FusionDomainVisualization,
  HingeDomainVisualization,
} from 'antibodies/AntibodyTypedefs';
import {getHingePathPoints} from 'antibodies/visualization/AntibodyHinge';
import {AntibodySiteMarkerProps} from 'antibodies/visualization/AntibodySiteMarker';
import {Point} from 'antibodies/visualization/linePathGeometry';
import {assert} from 'Asserts';
import {DomainNumbering, getAntibodyNumbering} from 'molbio/adcs/antibodyNumbering';
import {
  applyDomainTransform,
  besideLine,
  boxCenter,
  hingeLinePath,
  isBoxDomain,
  linePathOf,
  outsideEdgeLocalX,
  visibleRangeOfLine,
} from 'molbio/adcs/conjugation-site-markers/conjugationSiteMarkerUtils';
import {ConjugationSite} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteConstants';

// Tuning for the sizes of markers. A length is a fraction of the size of the whole glyph (the longer side of
// the viewbox), limited to a fraction of the width of a chain domain's box: see glyphRelativeLength.

const MARKER_GLYPH_RATIO = 0.035;
const MARKER_MAX_BOX_RATIO = 1 / 5;

// A line is considered hidden where it is within this distance of a chain domain's box, since markers that
// close would look as though they belonged to the domain.
const HIDDEN_LINE_MARGIN_GLYPH_RATIO = 0.04;
const HIDDEN_LINE_MARGIN_MAX_BOX_RATIO = 1 / 4;

// How far a marker beside a hinge overlaps the hinge's stroke, as a fraction of the stroke's thickness.
const LINE_MARKER_OVERLAP = 0.2;

// How far the marker for a line other than a hinge is moved off the line, to the side of it that its normal
// points to, in marker radii. Lines can cross (e.g. a diabody's two linkers cross at their middles), and
// markers exactly on them would then be stacked.
const LINE_CENTER_MARKER_OFFSET_IN_RADII = 0.7;

// A chain domain or hinge with more sites than this gets one marker for all of them, instead of one for each.
const MAX_INDIVIDUAL_SITES_PER_DOMAIN = 3;

/** The residues of a domain of the antibody. */
export interface DomainSequenceInfo {
  sequence: string;
  // The antibody numbering of the sequence, or null if it has none.
  numbering: DomainNumbering | null;
}

/**
 * The residues of each domain of the antibody, by the domain's format label (the label that a site names its
 * domain by). The copies of a domain, e.g. the two heavy chains' CH3, share a sequence.
 */
export type DomainSequenceInfoByLabel = Readonly<Record<string, DomainSequenceInfo>>;

/** The sites on one domain, with the domain's numbering, which the labels of the sites depend on. */
interface DomainSites {
  sequence: string;
  numbering: DomainNumbering | null;
  // Sorted by residue index.
  sites: Array<ConjugationSite>;
}

interface SitesOnDomain extends DomainSites {
  // One per drawn copy of the domain.
  vizes: Array<AntibodyFormatDomainVisualization>;
}

/**
 * The label of the marker for a site, which is shown when the marker is hovered: the label of the site's
 * domain, which includes the domain's chain, and the site's numbering, e.g. `CH2(Heavy) · K250`.
 */
function markerLabel(site: ConjugationSite, sequence: string, numbering: DomainNumbering | null): string {
  return `${site.domainLabel} · ${getAntibodyNumbering(sequence, numbering, site.residueIndex)}`;
}

function assertWithinSequence(site: ConjugationSite, sequence: string): void {
  assert(
    site.residueIndex < sequence.length,
    `Conjugation site is past the end of its domain's sequence: residue ${site.residueIndex} of ${sequence.length} in ${site.domainLabel}`
  );
}

/**
 * Group the sites by the domain they are on. The data model guarantees that each site's residue index
 * is a non-negative integer within its domain's sequence, that its domain label names a domain of the
 * format that the format draws, and that the domain has residues, so a site for which that isn't so is an
 * error.
 */
function groupSitesByDomain(
  format: AntibodyFormat,
  domainSequences: DomainSequenceInfoByLabel,
  sites: ReadonlyArray<ConjugationSite>
): Array<SitesOnDomain> {
  const groups = new Map<string, SitesOnDomain>();
  for (const site of sites) {
    assert(
      Number.isInteger(site.residueIndex) && site.residueIndex >= 0,
      `Conjugation site residue index must be a non-negative integer, not ${site.residueIndex}`
    );
    const existing = groups.get(site.domainLabel);
    if (existing != null) {
      assertWithinSequence(site, existing.sequence);
      existing.sites.push(site);
      continue;
    }
    const domain = format.domains.find((d) => d.label === site.domainLabel);
    assert(domain != null, `Conjugation site is on a domain not in the antibody format: ${site.domainLabel}`);
    // Every copy of the domain (e.g. both heavy chains' CH3) gets the sites.
    const vizes = domain.refs.flatMap((ref) => {
      const viz = format.visualization.domainVisualizations.find((v) => v.ref === ref);
      return viz == null ? [] : [viz];
    });
    assert(
      vizes.length > 0,
      `Conjugation site is on a domain the antibody format does not draw: ${site.domainLabel}`
    );
    const sequenceInfo = domainSequences[site.domainLabel];
    assert(
      sequenceInfo != null,
      `No amino acid sequence for the domain of a conjugation site: ${site.domainLabel}`
    );
    assertWithinSequence(site, sequenceInfo.sequence);
    groups.set(site.domainLabel, {
      vizes,
      sequence: sequenceInfo.sequence,
      numbering: sequenceInfo.numbering,
      sites: [site],
    });
  }
  return Array.from(groups.values(), (group) => ({
    ...group,
    sites: [...group.sites].sort((a, b) => a.residueIndex - b.residueIndex),
  }));
}

/**
 * A length, in viewbox units, that is `glyphRatio` of the size of the whole glyph (the longer side of its
 * viewbox) but no more than `maxBoxRatio` of the width of a chain domain's box.
 *
 * It is relative to the glyph, rather than only to a domain's box, because the glyph is scaled to fit the
 * screen: formats' viewboxes differ in size far more than their boxes do (an IgG's is about 4 times bigger
 * than an scFv's, and its boxes are only 1.4 times bigger), so a length that is only a fraction of a box
 * would look several times smaller on the IgG. The limit keeps a large glyph from giving a length that is
 * too big for the domains it is in.
 */
function glyphRelativeLength(format: AntibodyFormat, glyphRatio: number, maxBoxRatio: number): number {
  const {chainDomainSizing, svgSizing} = format.visualization.sizing;
  const glyphSize = Math.max(svgSizing.svgViewboxWidth, svgSizing.svgViewboxHeight);
  return Math.min(glyphRatio * glyphSize, maxBoxRatio * chainDomainSizing.width);
}

/** The radius of a marker, in viewbox units. */
function markerRadius(format: AntibodyFormat): number {
  return glyphRelativeLength(format, MARKER_GLYPH_RATIO, MARKER_MAX_BOX_RATIO);
}

/** How far from a chain domain's box a point on a line counts as hidden behind it, in viewbox units. */
function hiddenLineMargin(format: AntibodyFormat): number {
  return glyphRelativeLength(format, HIDDEN_LINE_MARGIN_GLYPH_RATIO, HIDDEN_LINE_MARGIN_MAX_BOX_RATIO);
}

/**
 * How far from the center line of a hinge the center of a marker is. The marker overlaps the hinge's stroke
 * by LINE_MARKER_OVERLAP of its thickness, close enough to read as on the hinge while leaving most of the
 * hinge clickable.
 */
function hingeMarkerOffset(format: AntibodyFormat): number {
  const {strokeThickness} = format.visualization.sizing.connectorSizing;
  return markerRadius(format) + strokeThickness / 2 - LINE_MARKER_OVERLAP * strokeThickness;
}

/**
 * Markers spaced evenly down the outside long edge of a chain domain (see {@link outsideEdgeLocalX}).
 * Chain domains are drawn as a box with the N-terminus at local y=0 and the C-terminus at y=height.
 */
function chainDomainMarkers(
  format: AntibodyFormat,
  viz: BlockDomainVisualization | FusionDomainVisualization,
  {numbering, sites, sequence}: DomainSites
): Array<AntibodySiteMarkerProps> {
  const {height} = format.visualization.sizing.chainDomainSizing;
  const radius = markerRadius(format);
  const localX = outsideEdgeLocalX(format, viz);
  return sites.map((site, index) => {
    const localY = ((index + 1) / (sites.length + 1)) * height;
    const {x, y} = applyDomainTransform(viz, {x: localX, y: localY}, boxCenter(format));
    return {x, y, radius, labels: [markerLabel(site, sequence, numbering)], count: 1};
  });
}

/**
 * Markers spaced evenly along the whole of a hinge, which runs from the outer end of its short part, through
 * the joint, to the end of its long part. They sit beside the hinge, on the outside of its bend (away from
 * the hinge's other end), so that the hinge itself stays clickable.
 */
function hingeMarkers(
  format: AntibodyFormat,
  viz: HingeDomainVisualization,
  {numbering, sites, sequence}: DomainSites
): Array<AntibodySiteMarkerProps> {
  const path = hingeLinePath(viz);
  const {start, joint, end} = getHingePathPoints(viz);
  const shortPartLength = Math.hypot(joint.x - start.x, joint.y - start.y);
  const radius = markerRadius(format);
  const offset = hingeMarkerOffset(format);
  return sites.map((site, index) => {
    const distance = ((index + 1) / (sites.length + 1)) * path.length;
    const {point, normal} = path.pointAndNormalAt(distance);
    // The inside of the bend is the side facing the far end of the hinge: the end of the long part for
    // a point on the short part, and the outer end of the short part for a point on the long part.
    const farEnd = distance <= shortPartLength ? end : start;
    const towardFarEnd = (farEnd.x - point.x) * normal.x + (farEnd.y - point.y) * normal.y;
    // Markers go on the outside of the bend, away from the far end. A straight hinge has no outside to its
    // bend; put its markers on the -x side.
    const flip = towardFarEnd > 0 || (towardFarEnd === 0 && normal.x > 0);
    const {x, y} = besideLine(viz, path, distance, flip ? -offset : offset);
    return {x, y, radius, labels: [markerLabel(site, sequence, numbering)], count: 1};
  });
}

/** The one marker for all of the sites of a domain, at `position`. */
function markerForSites(
  {numbering, sites, sequence}: DomainSites,
  position: Point,
  radius: number
): AntibodySiteMarkerProps {
  return {
    ...position,
    radius,
    labels: sites.map((site) => markerLabel(site, sequence, numbering)),
    count: sites.length,
  };
}

/** A marker for each site of a chain domain or hinge, at the places they are spaced along it. */
function individualMarkers(
  format: AntibodyFormat,
  viz: BlockDomainVisualization | FusionDomainVisualization | HingeDomainVisualization,
  domainSites: DomainSites
): Array<AntibodySiteMarkerProps> {
  return viz.__typename === 'HingeDomainVisualizationNode'
    ? hingeMarkers(format, viz, domainSites)
    : chainDomainMarkers(format, viz, domainSites);
}

/**
 * One marker for all of the sites of a chain domain or hinge, placed where the only marker would be if it had
 * one site.
 */
function countMarker(
  format: AntibodyFormat,
  viz: BlockDomainVisualization | FusionDomainVisualization | HingeDomainVisualization,
  domainSites: DomainSites
): AntibodySiteMarkerProps {
  const [marker] = individualMarkers(format, viz, {...domainSites, sites: [domainSites.sites[0]]});
  return markerForSites(domainSites, marker, marker.radius);
}

/**
 * One marker for all of the sites of a line other than a hinge, however many there are: halfway along the
 * part of it that is not hidden behind a chain domain, and a little way off the line.
 */
function lineMarker(
  format: AntibodyFormat,
  viz: AntibodyFormatDomainVisualization,
  domainSites: DomainSites
): AntibodySiteMarkerProps {
  const path = linePathOf(viz);
  const visible = visibleRangeOfLine(format, viz, path, hiddenLineMargin(format));
  const radius = markerRadius(format);
  const position = besideLine(
    viz,
    path,
    (visible.start + visible.end) / 2,
    LINE_CENTER_MARKER_OFFSET_IN_RADII * radius
  );
  return markerForSites(domainSites, position, radius);
}

/**
 * Compute markers for conjugation sites. See the module comment for how the sites of each domain are drawn.
 *
 * @param format The antibody format (glyph geometry and domain definitions).
 * @param domainResidues The residues and antibody numbering of each domain that has a site.
 * @param sites The sites to draw, from all of the antibody's conjugations.
 */
export function sitesToMarkers(
  format: AntibodyFormat,
  domainSequences: DomainSequenceInfoByLabel,
  sites: ReadonlyArray<ConjugationSite>
): Array<AntibodySiteMarkerProps> {
  return groupSitesByDomain(format, domainSequences, sites).flatMap(({vizes, ...domainSites}) =>
    vizes
      .map((viz) => {
        if (!isBoxDomain(viz) && viz.__typename !== 'HingeDomainVisualizationNode') {
          return [lineMarker(format, viz, domainSites)];
        }
        return domainSites.sites.length <= MAX_INDIVIDUAL_SITES_PER_DOMAIN
          ? individualMarkers(format, viz, domainSites)
          : [countMarker(format, viz, domainSites)];
      })
      .flat()
  );
}
